import {
  Button,
  DefaultAlertDialog,
  Text,
  useToast,
} from '~/common/components/defaultUIComponents';
import { Flex, Grid, GridItem, Select, useDisclosure } from '@chakra-ui/react';
import EmployeeApi from '~/common/api/zeiterfassungServerApi/EmployeeApi';
import { NewJobApi } from '~/common/api/zeiterfassungServerApi/NewJobApi';
import ProjectApi from '~/common/api/zeiterfassungServerApi/ProjectApi';
import { formatName } from '~/common/utils/employeeUtils';
import { ListView } from '~/common/components/ListView';
import useTranslate from '~/common/utils/translate';
import React, { createRef, useState } from 'react';
import { NewJobListItem } from './NewJobListItem';
import _ from 'lodash';

type FilterState = {
  employee?: number;
  project?: number;
};

export const NewJobsList: React.FC = () => {
  const [filterState, setFilterState] = useState<FilterState>({});
  const employeeRef = createRef<HTMLSelectElement>();
  const projectRef = createRef<HTMLSelectElement>();
  const t = useTranslate();

  const handleClick = () => {
    setFilterState((filterState) => {
      const newFilter = { ...filterState };
      if (employeeRef.current && projectRef.current) {
        if (employeeRef.current.value && employeeRef.current.value !== '-1') {
          newFilter.employee = parseInt(employeeRef.current.value);
        } else {
          newFilter.employee = undefined;
        }
        if (projectRef.current.value && projectRef.current.value !== '-1') {
          newFilter.project = parseInt(projectRef.current.value);
        } else {
          newFilter.project = undefined;
        }
      }
      return newFilter;
    });
  };

  const resetFilters = () => {
    setFilterState({});
    if (employeeRef.current && projectRef.current) {
      employeeRef.current.value = '-1';
      projectRef.current.value = '-1';
    }
  };

  const { isOpen, onToggle } = useDisclosure();

  const { mutateAsync: bulkDelete, isLoading: isDeleting } =
    NewJobApi.hooks.useBulkDeleteJobRecord();

  const toast = useToast({});

  const handleDeleteClick = async () => {
    const { employee, project } = filterState;
    if (employee || project) {
      await bulkDelete({ employee, project }).then(onToggle);
      toast({ title: 'deletion.success', status: 'success' });
    }
  };

  const [{ data: recordCount }] = NewJobApi.hooks.useNewJobRecordCount({
    refetchInterval: 1000 * 60,
  });

  const [{ data: newJobRecords, isLoading }, refetch] =
    NewJobApi.hooks.useNewJobRecords(filterState, {
      enabled: recordCount && recordCount.count > 0,
    });

  const [{ data: employees }] = EmployeeApi.hooks.useEmployeeQuery();
  const [{ data: project }] = ProjectApi.hooks.useProjectsQuery();

  React.useEffect(() => {
    if (
      recordCount &&
      newJobRecords &&
      recordCount.count > newJobRecords.length &&
      !isLoading
    ) {
      void refetch();
    }
  }, [recordCount]);

  return (
    <>
      <DefaultAlertDialog
        dialogHeaderText={'dashboard.jobRecord.bulkDelete.confirmation.header'}
        dialogBodyText={'dashboard.jobRecord.bulkDelete.confirmation'}
        isOpen={isOpen}
        onConfirmedAction={() =>
          void handleDeleteClick().catch(() =>
            toast({ title: 'generic.error', status: 'error' }),
          )
        }
        onCancel={onToggle}
        isLoading={isDeleting}
      />
      <Flex direction="row" justify="space-between" mb={3}>
        <Flex gap={3}>
          <Select
            ref={employeeRef}
            style={{
              width: '145px',
            }}
          >
            <option value="-1">{t('selectEmployee')}</option>
            {_.orderBy(employees, [(e) => formatName(e)], ['asc'])?.map((e) => (
              <option key={e.id} value={e.id}>
                {formatName(e)}
              </option>
            ))}
          </Select>
          <Select
            ref={projectRef}
            style={{
              width: '145px',
            }}
          >
            <option value="-1">{t('selectProject')}</option>
            {_.orderBy(project, ['name'], ['asc'])?.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </Select>

          <Flex gap={3} alignItems="center">
            {(filterState?.project || filterState?.employee) && (
              <Text
                textDecoration="underline"
                cursor="pointer"
                color="green.600"
                onClick={resetFilters}
                style={{ whiteSpace: 'nowrap' }}
              >
                resetFilter
              </Text>
            )}
            <Button onClick={handleClick}>search</Button>
            <Button
              disabled={!filterState.project && !filterState.employee}
              onClick={onToggle}
            >
              delete
            </Button>
          </Flex>
        </Flex>
      </Flex>
      <ListView
        data={newJobRecords}
        search=""
        searchFields={() => []}
        orderBy="newjob.project.name"
        render={({ data }) => (
          <Grid templateColumns="repeat(3, 1fr)" gap={3}>
            {data.map((record) => (
              <GridItem key={record.id}>
                <NewJobListItem record={record} />
              </GridItem>
            ))}
          </Grid>
        )}
      />
    </>
  );
};
